import type { TimetableSource } from "./timetableEndpoints";
import {
	timetableRequestIdentity,
	useTimetableRequestIdentity,
} from "./settingsQueries";
import {
	queryOptions,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	getClassTimetable,
	getRoomTimetable,
	getStaffTimetable,
	getStudentTimetable,
} from "./api";

export type StudentTimetableFetchParams = {
	studentNo: number;
	date?: string;
	source?: TimetableSource;
};

export const studentTimetableQueryOptions = (
	params: StudentTimetableFetchParams,
	identity = timetableRequestIdentity(),
) =>
	queryOptions({
		queryKey: ["timetable", "week", "student", ...identity, params],
		queryFn: () =>
			getStudentTimetable(params.studentNo, {
				date: params.date,
				source: params.source,
			}),
	});

export function useStudentTimetable(params: StudentTimetableFetchParams) {
	return useQuery(
		studentTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export function useStudentTimetableSuspense(
	params: StudentTimetableFetchParams,
) {
	return useSuspenseQuery(
		studentTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export type StaffTimetableFetchParams = {
	initials: string;
	date?: string;
	source?: TimetableSource;
};

export const staffTimetableQueryOptions = (
	params: StaffTimetableFetchParams,
	identity = timetableRequestIdentity(),
) =>
	queryOptions({
		queryKey: ["timetable", "week", "staff", ...identity, params],
		queryFn: () =>
			getStaffTimetable(params.initials, {
				date: params.date,
				source: params.source,
			}),
	});

export function useStaffTimetable(params: StaffTimetableFetchParams) {
	return useQuery(
		staffTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export function useStaffTimetableSuspense(params: StaffTimetableFetchParams) {
	return useSuspenseQuery(
		staffTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export const roomTimetableQueryOptions = (
	params: { roomCode: string; date?: string },
	identity = timetableRequestIdentity(),
) =>
	queryOptions({
		queryKey: ["timetable", "week", "room", ...identity, params],
		queryFn: () => getRoomTimetable(params.roomCode, { date: params.date }),
	});

export function useRoomTimetable(params: { roomCode: string; date?: string }) {
	return useQuery(
		roomTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export function useRoomTimetableSuspense(params: {
	roomCode: string;
	date?: string;
}) {
	return useSuspenseQuery(
		roomTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export const classTimetableQueryOptions = (
	params: { classId: number; date?: string },
	identity = timetableRequestIdentity(),
) =>
	queryOptions({
		queryKey: ["timetable", "week", "class", ...identity, params],
		queryFn: () => getClassTimetable(params.classId, { date: params.date }),
	});

export function useClassTimetable(params: { classId: number; date?: string }) {
	return useQuery(
		classTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}

export function useClassTimetableSuspense(params: {
	classId: number;
	date?: string;
}) {
	return useSuspenseQuery(
		classTimetableQueryOptions(params, useTimetableRequestIdentity()),
	);
}
